import React, { useState, useEffect } from 'react';
import {
  Create,
  usePermissions,
  useDataProvider,
  useNotify,
  useRedirect,
  useTranslate,
} from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Typography, Card, CardActionArea, CardContent, Stack } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MapIcon from '@mui/icons-material/Map';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { EventV2Form } from '../../components/form/EventV2Form';
import { EVENT_TYPES, DATE_TYPES } from '../../constants/events';
import { EVENT_CLONE_STORAGE_KEY } from '../../constants/eventClone';
import { getEventDbId, getEventRouteKey } from '../../utils/eventIdentity';

const EVENT_TYPE_META = {
  1: {
    icon: EventAvailableIcon,
    key: 'type1_desc',
    defaultDesc: 'Standard tickets without a seating or area map.',
  },
  2: {
    icon: MapIcon,
    key: 'type2_desc',
    defaultDesc: 'Sell tickets by area / zone on a venue layout.',
  },
  3: {
    icon: EventSeatIcon,
    key: 'type3_desc',
    defaultDesc: 'Assigned seats with a seating chart.',
  },
};

const DATE_TYPE_META = {
  oneoff: {
    icon: TodayIcon,
    key: 'oneoff_desc',
    defaultDesc: 'One single date and time for this event.',
  },
  session: {
    icon: DateRangeIcon,
    key: 'session_desc',
    defaultDesc: 'Multiple dates or sessions under one event.',
  },
};

/**
 * Event V2 Create Component
 * Wizard: Event Type → Date Type → Create form
 */
export const EventV2Create = (props) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [searchParams] = useSearchParams();
  const [eventType, setEventType] = useState(null);
  const [dateType, setDateType] = useState(null);
  const [cloneRecord, setCloneRecord] = useState(null);
  const [madeChoices, setMadeChoices] = useState(false);
  const [wizardStep, setWizardStep] = useState(0); // 0 = event type, 1 = date type

  useEffect(() => {
    let source = null;

    if (searchParams.get('clone') === '1') {
      try {
        const raw = sessionStorage.getItem(EVENT_CLONE_STORAGE_KEY);
        if (raw) {
          source = JSON.parse(raw);
          sessionStorage.removeItem(EVENT_CLONE_STORAGE_KEY);
        }
      } catch (error) {
        console.error('Invalid clone payload:', error);
      }
    } else if (searchParams.get('source')) {
      try {
        source = JSON.parse(searchParams.get('source'));
      } catch (error) {
        console.error('Invalid source param:', error);
      }
    }

    if (source?.type != null) {
      setEventType(source.type);
      setDateType(source.dateType);
      setCloneRecord(source);
      setMadeChoices(true);
    }
  }, [searchParams]);

  if (!permissions) return null;

  if (!madeChoices) {
    return (
      <EventTypeSelectionForm
        step={wizardStep}
        eventType={eventType}
        dateType={dateType}
        onEventTypeChange={setEventType}
        onDateTypeChange={setDateType}
        onBack={() => setWizardStep(0)}
        onNext={() => {
          if (wizardStep === 0) {
            if (!eventType) {
              alert(
                translate(
                  'create_wizard.select_event_type_first',
                  'Please select an event type first.',
                ),
              );
              return;
            }
            setWizardStep(1);
            return;
          }
          if (!dateType) {
            alert(
              translate('create_wizard.select_date_type_first', 'Please select a date type first.'),
            );
            return;
          }
          setMadeChoices(true);
        }}
      />
    );
  }

  return (
    <Create
      title={translate('create_wizard.new_event', 'New Event')}
      {...props}
      redirect={false}
      sx={{
        '& .RaCreate-main': {
          maxWidth: '100%',
          width: '100%',
        },
        '& .RaCreate-card': {
          maxWidth: '100%',
          width: '100%',
        },
      }}
      mutationOptions={{
        onSuccess: async (event) => {
          try {
            const eventDbId = getEventDbId(event);
            if (event?.layoutJSONID && eventDbId) {
              await dataProvider.update('eventLayouts', {
                id: event.layoutJSONID,
                data: { event: eventDbId },
              });
            }
            notify('Event created', { type: 'success' });
            redirect('list', 'events-v2');
          } catch (e) {
            console.error(e);
            notify('Event created but layout link failed — open edit and Save once', {
              type: 'warning',
            });
            redirect('edit', 'events-v2', getEventRouteKey(event));
          }
        },
      }}
    >
      <EventV2Form
        isEdit={false}
        permissions={permissions}
        eventType={eventType}
        dateType={dateType}
        cloneRecord={cloneRecord}
      />
    </Create>
  );
};

const OptionCard = ({ selected, title, description, icon: Icon, onClick }) => (
  <Card
    elevation={0}
    sx={{
      flex: '1 1 220px',
      maxWidth: 280,
      border: '2px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: 'transparent',
      boxShadow: 'none',
      transition: 'border-color 0.15s',
      '&:hover': {
        borderColor: selected ? 'primary.main' : 'primary.light',
      },
    }}
  >
    <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1,
          py: 3,
          px: 2,
          minHeight: 160,
        }}
      >
        {Icon ? (
          <Icon
            sx={{
              fontSize: 40,
              color: selected ? 'primary.main' : 'text.secondary',
              mb: 0.5,
            }}
          />
        ) : null}
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        ) : null}
      </CardContent>
    </CardActionArea>
  </Card>
);

/**
 * Wizard: pick event type (3 boxes), then date type (same box style), then Next → create form
 */
const EventTypeSelectionForm = ({
  step,
  eventType,
  dateType,
  onEventTypeChange,
  onDateTypeChange,
  onBack,
  onNext,
}) => {
  const translate = useTranslate();
  const isEventStep = step === 0;
  const canContinue = isEventStep ? Boolean(eventType) : Boolean(dateType);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        px: 2,
        pt: 2,
        pb: 4,
      }}
    >
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          width: '100%',
          maxWidth: 900,
          textAlign: 'center',
          bgcolor: 'transparent',
          boxShadow: 'none',
        }}
      >
        <Typography variant="h4" gutterBottom>
          {translate('create_wizard.new_event', { _: 'New Event' })}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {isEventStep
            ? translate('create_wizard.step1_desc', { _: 'Step 1 of 2 — Choose the event type.' })
            : translate('create_wizard.step2_desc', {
                _: 'Step 2 of 2 — Choose how dates work for this event.',
              })}
        </Typography>

        <Typography variant="h6" gutterBottom>
          {isEventStep
            ? translate('create_wizard.event_type', { _: 'Event Type' })
            : translate('create_wizard.date_type', { _: 'Event Date Type' })}
        </Typography>

        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          spacing={2}
          justifyContent="center"
          sx={{ mb: 3 }}
        >
          {isEventStep
            ? EVENT_TYPES.map((item) => {
                const meta = EVENT_TYPE_META[item.id] || {};
                const cardTitle = translate(`create_wizard.type_${item.id}_title`, {
                  _: item.name,
                });
                const desc = meta.key
                  ? translate(`create_wizard.${meta.key}`, { _: meta.defaultDesc })
                  : meta.defaultDesc;
                return (
                  <OptionCard
                    key={item.id}
                    selected={Number(eventType) === Number(item.id)}
                    title={cardTitle}
                    description={desc}
                    icon={meta.icon}
                    onClick={() => onEventTypeChange(item.id)}
                  />
                );
              })
            : DATE_TYPES.map((item) => {
                const meta = DATE_TYPE_META[item.id] || {};
                const cardTitle = translate(`create_wizard.${item.id}_title`, { _: item.name });
                const desc = meta.key
                  ? translate(`create_wizard.${meta.key}`, { _: meta.defaultDesc })
                  : meta.defaultDesc;
                return (
                  <OptionCard
                    key={item.id}
                    selected={dateType === item.id}
                    title={cardTitle}
                    description={desc}
                    icon={meta.icon}
                    onClick={() => onDateTypeChange(item.id)}
                  />
                );
              })}
        </Stack>

        <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center">
          {!isEventStep ? (
            <Button variant="outlined" onClick={onBack}>
              {translate('create_wizard.back', { _: 'Back' })}
            </Button>
          ) : null}
          <Button variant="contained" disabled={!canContinue} onClick={onNext}>
            {translate('create_wizard.next', { _: 'Next' })}
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" mt={2}>
          {isEventStep
            ? translate('create_wizard.step1_hint', { _: 'Select an event type, then click Next.' })
            : translate('create_wizard.step2_hint', {
                _: 'Select a date type, then click Next to continue to the create form.',
              })}
        </Typography>
      </Box>
    </Box>
  );
};
